// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useEffect } from 'react';
import { getGracePeriod, updateGracePeriod } from '@api/portalServices';
import type { GracePeriodGetResponse } from '@api/types';
import { Entitlement } from '@api/constants';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery } from '@tanstack/react-query';

const useEmitOnError = (error: ApiError | null) => {
  const { emitError } = useErrorContext();
  useEffect(() => {
    if (error) {
      emitError(new AppError(error));
    }
  }, [error, emitError]);
};

export const useGracePeriod = () => {
  const { tenantId, tenantInfo } = useSession();
  const hasSsoEntitlement = hasEntitlement(tenantInfo, Entitlement.SSO);

  const {
    data,
    isFetching,
    error,
    refetch,
  } = useQuery<GracePeriodGetResponse, ApiError>({
    queryKey: [QueryKey.GRACE_PERIOD, tenantId],
    queryFn: () => getGracePeriod({ contextType: 'endpoint-login' }, tenantId ?? ''),
    enabled: hasSsoEntitlement && Boolean(tenantId),
    retry: false,
    staleTime: 0,
  });

  const { mutateAsync: update, isPending } = useMutation<void, ApiError, number>({
    mutationFn: (gracePeriodSecs: number) =>
      Promise.all([
        updateGracePeriod({ gracePeriodSecs, contextType: 'endpoint-login' }, tenantId ?? ''),
        updateGracePeriod({ gracePeriodSecs, contextType: 'clinical-app-login' }, tenantId ?? ''),
      ]).then(() => undefined),
  });

  useEmitOnError(error);

  return {
    gracePeriodSecs: data?.gracePeriodSecs,
    isFetching,
    isPending,
    error,
    refetch,
    update,
  };
};
